import {
  Button,
  Checkbox,
  CircularProgress,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  Grid,
  Icon,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import withStyles from '@material-ui/core/styles/withStyles'
import React, { useState } from 'react'

import Factory from '../../atom/FieldFactory/FieldFactory'

function SynchronizationPage({ api, data, classes }) {
  // Initial setup of fields for synchronization settings
  const initialFields = FIELDS_SETUP.map((field) => {
    return {
      ...field,
      options: field.options.filter(({ value }) => value !== data.local_db_name),
      value: data[field.name] || '',
      error: false,
    }
  })

  const [fields, updateFields] = React.useState(initialFields)
  const [fetching, setFetching] = React.useState(false)
  const [comparisonData, setComparisonData] = React.useState([])
  const [error, setError] = React.useState(null)

  const totalDocsToSync = comparisonData.reduce((acc, { data }) => acc + data.length, 0)

  const handleSetupChange = (e) => {
    updateFields((prevFields) => {
      return prevFields.map((f) => {
        if (f.name !== e.target.name) return f
        return { ...f, error: false }
      })
    })
  }

  const handleCheckboxChange = (e) => {
    if (e.target.checked) setError(false)
  }

  const getField = (name) => document.forms.synch_form?.elements?.[name] || {}

  const validateFields = () => {
    const updates = structuredClone(fields)
    updates.forEach((field) => {
      const { value, ariaInvalid } = getField(field.name)
      if (!value || ariaInvalid === 'true') field.error = true
    })
    return updates.every((field) => !field.error)
  }

  const analyseData = async () => {
    const isSetupValid = validateFields()
    if (!isSetupValid) return

    // Turn on fetching state
    setError(null)
    setComparisonData([])
    setFetching(true)

    try {
      // Save settings in the database
      const update = fields.reduce((acc, { name }) => {
        acc[name] = getField(name).value || ''
        return acc
      }, {})
      const { request: updateRequest } = api.updateModelEntry(`/@/~/technical/${data.documentId}`, update)
      await updateRequest

      // API call to fetch comparison data
      const { request: comparisonDataRequest } = api.getSyncComparisonData()
      const fetchedData = await comparisonDataRequest
      setComparisonData(fetchedData)
    } catch (err) {
      console.error('Error during data analysis:', err)
      setError(err)
    }

    // Reset fetching state
    setFetching(false)
  }

  const syncData = async () => {
    const confirmed = window.confirm(
      '⚠️ Are you sure you want to sync now? ' +
        'All the selected entries will be copied. ' +
        'THIS ACTION CAN’T BE UNDONE.',
    )
    if (!confirmed) return
    setFetching(true)
    try {
      const itemsToSync = {}
      let totalSelected = 0
      for (const { collection } of comparisonData) {
        const { elements } = document.forms[collection] || {}
        if (!elements || !elements.length) continue
        itemsToSync[collection] = Array.from(document.forms[collection].elements)
          .filter(({ checked }) => checked)
          .map(({ name }) => name)
        totalSelected += itemsToSync[collection].length
      }
      if (!totalSelected) throw new Error('Please select at least one item to sync')
      const { request: syncDataRequest } = api.syncDataFromPeerEnvironment(itemsToSync)
      await syncDataRequest
      setComparisonData([])
    } catch (err) {
      console.error('Error during data synchronization:', err)
      setError(err)
    } finally {
      setFetching(false)
      analyseData()
    }
  }

  return (
    <>
      <div className={classes.root}>
        <Typography variant="h4" style={{ lineHeight: 1 }}>
          Data Sync
        </Typography>
      </div>

      <Paper className={classes.block} style={{ marginBottom: 10 }}>
        <Typography variant="h5">Synchronization Settings</Typography>
        <Grid component="form" id="synch_form" container spacing={24} style={{ marginTop: 10 }}>
          {fields.map((field, i) => (
            <Grid item xs={12} md={6} key={i}>
              {Factory.getFieldByType({
                ...field,
                initialValue: data[field.name] || '',
                onChange: handleSetupChange,
              })}
            </Grid>
          ))}
        </Grid>
        <footer style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="outlined" onClick={analyseData}>
            <Icon>preview</Icon>
            <span style={{ marginLeft: 6 }}>Preview</span>
          </Button>
          <Button
            variant="outlined"
            disabled={fetching || !comparisonData.length || totalDocsToSync === 0}
            onClick={syncData}
          >
            <Icon>sync</Icon>
            <span style={{ marginLeft: 6 }}>Sync</span>
          </Button>
        </footer>
      </Paper>

      {(comparisonData.length || fetching || error) && (
        <Paper className={classes.block}>
          <Typography variant="h4">Sync Preview {fetching && <CircularProgress size={16} />}</Typography>
          {error && (
            <Typography variant="body1" color="error" className={classes.error}>
              <Icon color="error">error</Icon> Error during synchronization: {error.message || 'Unknown error'}
            </Typography>
          )}
          {comparisonData.map((group) => (
            <CollectionFormTable
              key={group.collection}
              classes={classes}
              handleCheckboxChange={handleCheckboxChange}
              {...group}
            />
          ))}
        </Paper>
      )}
    </>
  )
}

function CollectionFormTable({ classes, collection, columns, data, handleCheckboxChange }) {
  const [isExpanded, setIsExpanded] = useState(false)

  if (!data.length) {
    return (
      <Typography variant="h6" className={classes.subtitle}>
        Collection: <b>{collection}</b> - no items to be added
      </Typography>
    )
  }

  return (
    <form id={collection}>
      <ExpansionPanel
        classes={{ root: classes.expansionPanelRoot }}
        expanded={isExpanded}
        elevation={0}
        onChange={() => {
          setIsExpanded(!isExpanded)
        }}
      >
        <ExpansionPanelSummary
          classes={{ root: classes.expansionPanelSummaryRoot }}
          expandIcon={<Icon>expand_more</Icon>}
        >
          <Typography variant="h6" className={classes.subtitle}>
            Collection: <b>{collection}</b> - {data.length + ' item(s) to be added'}
          </Typography>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails classes={{ root: classes.expansionPanelDetailsRoot }}>
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  {columns.map((name) => (
                    <TableCell key={name}>{name}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell padding="checkbox">
                      <Checkbox name={row._id} onChange={handleCheckboxChange} />
                    </TableCell>
                    {columns.map((name) => (
                      <TableCell padding="dense" key={`${name}_${index}`}>
                        {row[name]
                          ? typeof row[name] !== 'string'
                            ? JSON.stringify(row[name])
                            : row[name]
                          : ''}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </ExpansionPanelDetails>
      </ExpansionPanel>
    </form>
  )
}

const FIELDS_SETUP = [
  {
    type: 'enum',
    label: 'Peer database name',
    name: 'sync_db_name',
    options: [
      { value: 'jti-cxm', label: 'jti-cxm' },
      { value: 'jti-cxm-qa', label: 'jti-cxm-qa' },
    ],
    required: true,
    helperText: 'Select the name for the peer database',
  },
  {
    type: 'multiple',
    label: 'Collections to synchronize',
    name: 'sync_collections',
    options: [
      { value: 'portallabels', label: 'Labels' },
      { value: 'messages', label: 'Messages & Errors' },
      { value: 'sys_notifications', label: 'Notifications' },
    ],
    required: true,
    helperText: 'Select the collections you want to synchronize',
  },
]

const styles = {
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: -12,
    zIndex: 999,
    backgroundColor: 'white',
    padding: '16px 20px 10px',
    margin: -12,
    marginBottom: 12,
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.2)',
  },
  block: {
    padding: 20,
  },
  error: {
    marginTop: 10,
    display: 'flex',
    gap: '10px',
  },
  subtitle: {
    marginTop: 20,
    marginBottom: 10,
  },
  expansionPanelRoot: { marginTop: 20 },
  expansionPanelSummaryRoot: { padding: 0 },
  expansionPanelDetailsRoot: { padding: 0 },
}

export default withStyles(styles)(SynchronizationPage)
